import "../../../src/scss/editorControls.scss";
import { itx } from "../../../src/translationItx/itx";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { C1_LightProductImage } from "./C1_LightProductImage";
import {
  getLightProductDescription,
  getLightProductImageUrl,
  type C1_LightProductId,
} from "../data/C1_ProductDefinitions";

type C1_LightProductSelectProps = {
  value?: C1_LightProductId;
  options: C1_LightProductId[];
  onChange: (value: C1_LightProductId | undefined) => void;
};

export function C1_LightProductSelect({
  value,
  options,
  onChange,
}: C1_LightProductSelectProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);
  const [optionsPosition, setOptionsPosition] = useState<{
    left: number;
    top: number;
    width: number;
    maxHeight: number;
    openAbove: boolean;
  }>();
  const selectedDescription = value ? getLightProductDescription(value) : itx("c1.ui.Nicht_festgelegt", "Nicht festgelegt");
  const selectedImageUrl = value ? getLightProductImageUrl(value) : undefined;

  useEffect(() => {
    if (!open) return;
    const closeOnOutsidePointer = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!rootRef.current?.contains(target) && !optionsRef.current?.contains(target)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", closeOnOutsidePointer);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsidePointer);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) {
      setOptionsPosition(undefined);
      return;
    }

    const updatePosition = () => {
      const button = buttonRef.current;
      if (!button) return;
      const rect = button.getBoundingClientRect();
      const fieldRect = rootRef.current?.closest("label")?.getBoundingClientRect() ?? rect;
      const viewportPadding = 8;
      const gap = 4;
      const availableBelow = window.innerHeight - rect.bottom - viewportPadding - gap;
      const availableAbove = rect.top - viewportPadding - gap;
      const openAbove = availableBelow < 240 && availableAbove > availableBelow;
      const availableHeight = openAbove ? availableAbove : availableBelow;
      const width = Math.min(fieldRect.width, window.innerWidth - viewportPadding * 2);
      const left = Math.max(viewportPadding, Math.min(fieldRect.left, window.innerWidth - viewportPadding - width));
      setOptionsPosition({
        left,
        top: openAbove ? rect.top - gap : rect.bottom + gap,
        width,
        maxHeight: Math.max(120, Math.min(360, availableHeight)),
        openAbove,
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open]);

  const choose = (nextValue: C1_LightProductId | undefined) => {
    onChange(nextValue);
    setOpen(false);
  };

  return (
    <div className="C1_lightProductSelect" ref={rootRef}>
      <button
        ref={buttonRef}
        type="button"
        className="C1_lightProductSelectButton estada-editorControl"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown") {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        {selectedImageUrl && <C1_LightProductImage src={selectedImageUrl} />}
        <span>{selectedDescription}</span>
        <span className="C1_lightProductSelectChevron" aria-hidden="true" />
      </button>
      {open && typeof document !== "undefined" && createPortal(
        <div
          ref={optionsRef}
          className="C1_lightProductSelectOptions C1_lightProductSelectOptions--portal dockTypography"
          role="listbox"
          aria-label={itx("c1.ui.Bauform", "Bauform")}
          style={{
            position: "fixed",
            left: optionsPosition?.left ?? 0,
            top: optionsPosition?.top ?? 0,
            width: optionsPosition?.width,
            maxHeight: optionsPosition?.maxHeight,
            transform: optionsPosition?.openAbove ? "translateY(-100%)" : undefined,
            visibility: optionsPosition ? "visible" : "hidden",
          }}
        >
          <button
            type="button"
            role="option"
            aria-selected={!value}
            className={!value ? "C1_selected" : undefined}
            onClick={() => choose(undefined)}
          >
            <span className="C1_lightProductSelectImagePlaceholder" />
            <span>{itx("c1.ui.Nicht_festgelegt", "Nicht festgelegt")}</span>
          </button>
          <div className="C1_lightProductSelectGroup" role="group">
            {options.map((productId) => {
              const imageUrl = getLightProductImageUrl(productId);
              return (
                <button
                  type="button"
                  role="option"
                  aria-selected={value === productId}
                  className={value === productId ? "C1_selected" : undefined}
                  key={productId}
                  onClick={() => choose(productId)}
                >
                  {imageUrl ? (
                    <C1_LightProductImage src={imageUrl} />
                  ) : (
                    <span className="C1_lightProductSelectImagePlaceholder" />
                  )}
                  <span>{getLightProductDescription(productId)}</span>
                </button>
              );
            })}
          </div>
        </div>,
        rootRef.current?.closest(".C1") ?? document.body,
      )}
    </div>
  );
}
